import type { KeyboardEvent } from "react"
import { Box, MenuItem, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { HoverActionMenu } from "../../../../../../shared/components/table/HoverActionMenu"

import { PlannerTag, PlannerTagVariant } from "../../weekMenu/components/PlannerTag"
import { MenuTemplateListItem } from "../menuTemplatesTypes"

const useStyles = makeStyles()((theme) => ({
    row: {
        width: "100%",
        padding: "1rem",
        cursor: "pointer",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".625rem",
        background: theme.palette.background.paper,
        textAlign: "left",
        transition: theme.transitions.create(["border-color", "box-shadow"], {
            duration: theme.transitions.duration.short,
        }),
        "&:hover": {
            borderColor: theme.palette.tailwind.slate[400],
        },
    },
    rowContent: {
        width: "100%",
    },
    main: {
        minWidth: 0,
    },
    name: {
        fontWeight: 700,
    },
    meta: {
        color: theme.palette.tailwind.slate[500],
    },
    noChannels: {
        color: theme.palette.tailwind.slate[500],
    },
    menuButton: {
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    template: MenuTemplateListItem
    onOpen: () => void
    onRemove: () => void
}>

export function TemplateListRow({ template, onOpen, onRemove }: Props) {
    const { classes } = useStyles()

    const channels = template.channels ?? []

    // Enter/Space activation, matching the native <button> behaviour role="button" only announces.
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        onOpen()
    }

    return (
        <Box className={classes.row} role="button" tabIndex={0} onClick={onOpen} onKeyDown={handleKeyDown}>
            <Stack direction="row" alignItems="center" spacing="1rem" className={classes.rowContent}>
                <Stack className={classes.main} flexGrow={1}>
                    <Typography variant="subtitle1" className={classes.name}>
                        {template.name}
                    </Typography>
                    <LocalizedStrict
                        id="merchant-menu-templates-row-meta"
                        vars={{ categories: template.categoryCount, dishes: template.dishCount }}
                    >
                        <Typography variant="caption" className={classes.meta}>
                            {template.categoryCount} categories · {template.dishCount} dishes
                        </Typography>
                    </LocalizedStrict>
                </Stack>

                {channels.length > 0 ? (
                    <Stack direction="row" spacing=".5rem" flexWrap="wrap">
                        {channels.map((channel) => (
                            <PlannerTag key={channel} variant={PlannerTagVariant.GLOBAL}>
                                <LocalizedEnum base="merchant-menu-templates-channel" enumValue={channel}>
                                    <span>{channel}</span>
                                </LocalizedEnum>
                            </PlannerTag>
                        ))}
                    </Stack>
                ) : (
                    <LocalizedStrict id="merchant-menu-templates-row-no-channels">
                        <Typography variant="caption" className={classes.noChannels}>
                            No channel
                        </Typography>
                    </LocalizedStrict>
                )}

                <LocalizedStrict id="merchant-menu-templates-row-edit-button">
                    <StandardButton fullWidth={false} size="small" variant="outlined" onClick={onOpen}>
                        Edit
                    </StandardButton>
                </LocalizedStrict>
                <HoverActionMenu iconButtonClassName={classes.menuButton} onOpen={(event) => event.stopPropagation()}>
                    {(closeMenu) => (
                        <LocalizedStrict id="merchant-menu-templates-row-delete-item">
                            <MenuItem
                                onClick={(event) => {
                                    event.stopPropagation()
                                    closeMenu()
                                    onRemove()
                                }}
                            >
                                Delete
                            </MenuItem>
                        </LocalizedStrict>
                    )}
                </HoverActionMenu>
            </Stack>
        </Box>
    )
}
